/** @format */

import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
	InvalidResponse,
	ReviewFilter,
	ReviewPagination,
	ReviewService,
	ReviewSummary,
	ReviewsPaginationReturn,
	SelectedProductReviewsProsCons,
	addProductReviewsMultimedia,
	selectProductReviewProductIdentifier,
	selectProductReviewSummary,
	selectProductReviews,
	selectReviewFilters,
	selectSearchFilter,
	selectSelectedFeature,
	selectSortFilter,
	setProductReviews
} from '@product-reviews/data-access';
import { Observable, Subject, combineLatest, finalize, of } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { KillswitchService, UtilityService } from '@shared/data-access/services';

import { MediaSourceEnum } from '../models';
import { Store } from '@ngrx/store';

import { chain } from '@core/chain';
import { selectKillswitch } from '@pdp/data-access/state';

/** @format */

@Component({
	selector: 'product-reviews-drawer-container',
	templateUrl: './reviews-drawer-container.component.html',
	styleUrl: './reviews-drawer-container.component.scss',
	standalone: false
})
export class ReviewsDrawerContainerComponent implements OnInit, OnDestroy {
	private readonly store = inject(Store);
	private readonly reviewService = inject(ReviewService);
	private readonly utilityService = inject(UtilityService);
	private readonly killSwitchService = inject(KillswitchService);

	@Input() isFirstPanelOpen;

	private UNSUBSCRIBE$ = new Subject<void>();
	writeAReviewLabel = 'Write A Review';
	writeAReviewType = 'button';
	loadMoreLabel = '';
	starsOptions = {
		width: '20px',
		height: '20px'
	};
	rating = 0;
	percentage = 0;
	count = 0;
	reviewsPaginated: ReviewsPaginationReturn;
	productId = '';
	isLoadMoreRequesting = false;
	mediaSource = MediaSourceEnum.DRAWER;
	searchFilter = '';
	selectedFilters: ReviewFilter[] = [];
	selectedSort = '';
	hasNewReviewsEnhancement = false;
	reviewsFilters = false;
	reviewsProsCons = false;
	selectedTag = '';
	selectedFeature: SelectedProductReviewsProsCons;

	isLoading = false;
	selectProductId$ = this.store.select(selectProductReviewProductIdentifier);
	selectFilters$ = this.store.select(selectReviewFilters);
	selectSort$ = this.store.select(selectSortFilter);
	selectProConFeature$ = this.store.select(selectSelectedFeature);
	selectSearch$ = this.store.select(selectSearchFilter);
	selectReviewSummary$ = this.store
		.select(selectProductReviewSummary)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	selectReviews$ = this.store.select(selectProductReviews);
	shouldSaveReviewsAsDefault = false;
	isNewReviewsEnabled: boolean;
	isReviewsAISummaryEnabled = this.store.selectSignal(
		selectKillswitch('isReviewsAISummaryEnabled')
	);
	isHighlyRatedReviewsEnabled = this.store.selectSignal(
		selectKillswitch('isHighlyRatedReviewsEnabled')
	);

	ngOnInit(): void {
		this.handleKillSwitch();
		this.selectReviewSummary$.subscribe((reviewSummary: ReviewSummary) => {
			this.rating = reviewSummary.averageRating;
			this.count = reviewSummary.reviewCount;
			this.percentage = reviewSummary.recommendationPercentage;
		});

		this.setupReviewsStore$().subscribe(
			(reviewsPaginated) => {
				if (this.shouldSaveReviewsAsDefault) {
					this.store.dispatch(setProductReviews({ reviews: reviewsPaginated.reviews }));
					this.shouldSaveReviewsAsDefault = false;
				}

				if (reviewsPaginated.reviews) {
					this.loadMoreLabel = this.getLoadMoreLabel(reviewsPaginated.pagination);
					this.reviewsPaginated = { ...reviewsPaginated };
				}

				this.isLoadMoreRequesting = false;
			},
			() => {
				this.isLoadMoreRequesting = !this.isLoadMoreRequesting;
			}
		);
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	loadMoreReviews(): void {
		const { pagination } = this.reviewsPaginated;
		this.isLoadMoreRequesting = true;
		this.isLoading = true;

		this.requestReviews(pagination.to, pagination.perPage)
			.pipe(
				take(1),
				finalize(() => (this.isLoading = false))
			)
			.subscribe(
				(reviewsPaginated: ReviewsPaginationReturn) => {
					if (reviewsPaginated.reviews.length) {
						this.loadMoreLabel = this.getLoadMoreLabel(reviewsPaginated.pagination);

						this.reviewsPaginated = {
							...reviewsPaginated,
							reviews: [...this.reviewsPaginated.reviews, ...reviewsPaginated.reviews]
						};
					}
					this.isLoadMoreRequesting = !this.isLoadMoreRequesting;
				},
				() => {
					this.isLoadMoreRequesting = !this.isLoadMoreRequesting;
				}
			);
	}

	setupReviewsStore$(): Observable<ReviewsPaginationReturn> {
		return combineLatest([
			this.selectProductId$,
			this.selectReviews$,
			this.selectFilters$,
			this.selectSort$,
			this.selectSearch$,
			this.selectReviewSummary$,
			this.selectProConFeature$
		]).pipe(
			takeUntil(this.UNSUBSCRIBE$),
			debounceTime(300),
			filter(([productId]) => !!productId),
			tap(([, , filters, sortBy, searchFilter, , feature]) => {
				this.searchFilter = typeof searchFilter === 'string' ? searchFilter : '';
				this.selectedFilters = filters instanceof InvalidResponse ? [] : filters;
				this.selectedSort =
					sortBy instanceof InvalidResponse ? '' : `${sortBy.name}:${sortBy.value}`;
				this.selectedFeature = feature instanceof InvalidResponse ? null : feature;
				this.isLoading = true;
			}),
			switchMap(
				([
					productId,
					reviews,
					filters,
					sortBy,
					searchFilter,
					reviewSummary,
					proConFeature
				]) => {
					this.productId = productId;
					this.isLoadMoreRequesting = true;

					// If there is not any filter stored by PDP or Drawer return the 15 reviews already requested in PDP.
					if (
						filters instanceof InvalidResponse &&
						sortBy instanceof InvalidResponse &&
						searchFilter instanceof InvalidResponse &&
						proConFeature instanceof InvalidResponse
					) {
						const totalReviews =
							reviewSummary instanceof InvalidResponse
								? reviews.length
								: reviewSummary.reviewCount;

						if (this.isFirstPanelOpen && this.isNewReviewsEnabled) {
							this.getReviewImages(productId);
							if (reviews.length < 15 && totalReviews >= 15) {
								this.shouldSaveReviewsAsDefault = true;
								return this.requestReviews();
							}
							this.isFirstPanelOpen = false;
						}

						const pagination = this.reviewService.createPagination(totalReviews, 0, 15);

						return of({ reviews, pagination });
					}

					// Else, if any filter is stored by PDP or Drawer then request new 15 reviews.
					return this.requestReviews();
				}
			),
			tap(() => (this.isLoading = false))
		);
	}

	getReviewImages(productIdentifier: string): void {
		this.reviewService
			.getReviewsMultimedia$(productIdentifier)
			.subscribe((reviewsMultimedia) => {
				if (reviewsMultimedia instanceof InvalidResponse) {
					return;
				}

				this.store.dispatch(addProductReviewsMultimedia({ reviewsMultimedia }));
			});
	}

	private requestReviews(offset = 0, limit = 15): Observable<ReviewsPaginationReturn> {
		const reviewsOptions = {
			offset,
			limitTo: limit,
			filter: this.selectedFilters,
			sortBy: this.selectedSort,
			search: this.searchFilter,
			feature: null
		};

		if (this.isHighlyRatedReviewsEnabled() && this.selectedFeature) {
			reviewsOptions.feature = this.selectedFeature.feature;
		}

		return this.reviewService.getReviewsResults$(this.productId, reviewsOptions);
	}

	private getLoadMoreLabel({
		currentPage,
		lastPage,
		secondLastPage,
		perPage,
		total,
		to
	}: ReviewPagination): string {
		let nextResults = perPage;

		if (currentPage === secondLastPage) {
			nextResults = total - to;
		} else if (currentPage === lastPage) {
			nextResults = 0;
		}

		return `Load ${nextResults} more reviews`;
	}

	private handleKillSwitch(): void {
		this.killSwitchService
			.getKillSwitchesEnvironment$()
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				map((data) => [
					this.utilityService.checkKillSwitch(
						'newReviewsEnhancement',
						data,
						chain.chainIdentifierAbbr
					),
					this.utilityService.checkKillSwitch(
						'reviewsProsCons',
						data,
						chain.chainIdentifierAbbr
					),
					this.utilityService.checkKillSwitch(
						'reviewsFilters',
						data,
						chain.chainIdentifierAbbr
					),
					this.utilityService.checkKillSwitch(
						'isNewReviewsEnabled',
						data,
						chain.chainIdentifierAbbr
					)
				])
			)
			.subscribe(
				([
					hasNewReviewsEnhancement,
					reviewsProsCons,
					reviewsFilters,
					isNewReviewsEnabled
				]) => {
					this.hasNewReviewsEnhancement = hasNewReviewsEnhancement;
					this.reviewsFilters = reviewsFilters;
					this.reviewsProsCons = reviewsProsCons;
					this.isNewReviewsEnabled = isNewReviewsEnabled;
				}
			);
	}
}
